// @flow

import { Component } from '~/sources/scripts/jsti/core';
import Template from '../../classes/Template';
import Product from '../../classes/Product';
import { clearChildElements } from '../../classes/Helpers';
import PictureMedia from '../../classes/PictureMedia';
import Image from '../../classes/Image';

export const isSimpleProduct = (product: Object): boolean => {
  return !Array.isArray(product.variants);
};

export default class ProductGenerator extends Component {
  #template: HTMLElement;
  allowOriginalPrices: boolean = true;
  uspBar: HTMLElement;
  uspIntervalCount: Number = 6;
  selectionAid: HTMLElement;
  selectionAidIntervalCount: Number = 12;

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    const template: HTMLTemplateElement | null = this.el.querySelector('template[ref="product"]');
    if (!template) {
      throw new Error('A template tag is required');
    }

    this.#template = Template.getContent(template);
    const uspBarTemplate: HTMLTemplateElement | null = this.el.querySelector('template[ref="usp-bar"]');
    if (uspBarTemplate) {
      this.uspBar = Template.getContent(uspBarTemplate);
    }

    const selectionAidTemplate: HTMLTemplateElement | null = this.el.querySelector('template[ref="selection-aid"]');
    if (selectionAidTemplate) {
      this.selectionAid = Template.getContent(selectionAidTemplate);
    }
  }

  /**
   * Create new products and append them to the element of the component
   */
  createNewProducts(
    products: Array<Object>,
    dataLayerProducts: Array<Object>,
    insiderVariationId: number | null = null,
    location: string | null = null,
    extraProducts: Array<string> | null = null
  ): void {
    clearChildElements(this.el);
    let uspInterval = this.uspIntervalCount;
    let selectionAidInterval = this.selectionAidIntervalCount;

    products.forEach((product: Object, index: number) => {
      if (uspInterval === index && this.uspBar) {
        this.el.appendChild(this.uspBar.cloneNode(true));
        uspInterval += this.uspIntervalCount;
      }

      if (selectionAidInterval === index && this.selectionAid) {
        this.el.appendChild(this.selectionAid.cloneNode(true));
        selectionAidInterval += this.selectionAidIntervalCount;
      }

      this.el.appendChild(
        this.newProduct(product, dataLayerProducts[index], insiderVariationId, location, extraProducts)
      );
    });
  }

  /**
   * Create a single new product
   */
  newProduct(data: Object, datalayerData: Object, insiderVariationId: number | null, location: string | null, extraProducts: Array<string> | null): HTMLElement {
    const product: Product = new Product(this.#template.cloneNode(true));
    insiderVariationId
      ? product.href = data.url
      : product.slug = data.slug;
    product.sku = data.sku;
    product.image = data.image;
    product.title = data.title;
    product.rating = data.ratings;
    product.setEventData(data, datalayerData, insiderVariationId, location);
    product.promotionLabel = data.promotion_label;

    let lowestPriceUnitObject = data;
    let lowestPriceObject = data;
    let highestPriceObject = data;
    if (!isSimpleProduct(data)) {
      lowestPriceUnitObject = data.variants.filter((variant: Object) => variant.price_unit && variant.unit)
        .sort((a: Object, b: Object) => a.price_unit - b.price_unit)
        .find(Boolean);

      lowestPriceObject = data.variants.filter((variant: Object) => variant.price)
        .sort((a: Object, b: Object) => a.price - b.price)
        .find(Boolean);

      highestPriceObject = data.variants.filter((variant: Object) => variant.price)
        .sort((a: Object, b: Object) => a.price - b.price)
        .reverse()
        .find(Boolean);
    }

    product.price = lowestPriceObject ? lowestPriceObject.price : null;

    lowestPriceObject && highestPriceObject && highestPriceObject.price && highestPriceObject.price !== lowestPriceObject.price ? product.maxPrice = highestPriceObject.price : product.removeMaxPrice();

    const originalPricesObject = {
      price: lowestPriceObject ? lowestPriceObject.price : 0,
      priceMax: highestPriceObject ? highestPriceObject.price : 0,
      price_original: lowestPriceObject ? lowestPriceObject.price_original : 0,
      price_max_original: highestPriceObject ? highestPriceObject.price_original : 0
    };

    if (!lowestPriceObject || !this.shouldShowOriginalPrices(originalPricesObject)) {
      product.removeOriginalMaxPrice();
      product.removeOriginalPrice();
    } else {
      product.originalPrice = lowestPriceObject.price_original;
      highestPriceObject.price_original !== lowestPriceObject.price_original
        ? product.originalMaxPrice = highestPriceObject.price_original
        : product.removeOriginalMaxPrice();
    }

    if (product.thumbnails) {
      if (!data.show_mini_thumbnails) {
        product.removeOuterClass('thumbnails');
        product.thumbnails.innerHTML = '';
      } else {
        this.addThumbnails(data, product.thumbnails);
      }
    }

    if (lowestPriceUnitObject) {
      product.setUnitPrice(lowestPriceUnitObject.price_unit, lowestPriceUnitObject.unit);
    } else {
      product.removeUnitPrice();
    }

    product.updateAddToCart(data, datalayerData, location);

    if (insiderVariationId && (!extraProducts || !extraProducts.includes(data.sku))) {
      product.addInsiderInformation(data.simpleSku, data.url);
    }

    if (!data.has_subscriptions) {
      product.removeRepeatLabel();
    }
    return product.get();
  }

  /**
   * Check if the product should show the original prices
   */
  shouldShowOriginalPrices(data: Object): boolean {
    if (!this.allowOriginalPrices || (!data.price_original && !data.price_max_original) || !data.priceMax) {
      return false;
    }

    return data.price_original !== data.price || data.price_max_original !== data.priceMax;
  }

  /**
   *
   */
  addThumbnails(productData: Object, thumbnails: HTMLElement): void {
    const maxItems = thumbnails.dataset.maxItems;
    const thumbnailList = thumbnails.getElementByRef('thumbnail-list');
    const template = thumbnails.getElementByRef('thumbnail').cloneNode(true);
    const thumbnailData = Array.isArray(productData.mini_thumbnails)
      ? productData.mini_thumbnails
      : Object.values(productData.mini_thumbnails);
    thumbnailList.innerHTML = '';

    thumbnailData.some(({ src, alt }: Image, index: number) => {
      if (index >= maxItems) {
        const thumbnail = template.cloneNode(true);
        thumbnail.innerHTML = `
          <span class="text-xs md:text-sm text-black font-semibold">
            +${thumbnailData.length - maxItems}
          </span>
        `;
        thumbnailList.appendChild(thumbnail);

        return true;
      }

      const thumbnail = template.cloneNode(true);
      const picture = thumbnail.getElementByRef('picture');
      const image = window.store.cdn.image(`/${src}`, alt);
      picture.innerHTML = new PictureMedia(picture, image).build().innerHTML;
      thumbnailList.appendChild(thumbnail);

      return false;
    });
  }
}
